/**
 * OpenAI Keys Command
 * Show OpenAI-compatible API credentials and info
 */
import { Command } from 'commander';
import chalk from 'chalk';
import boxen from 'boxen';
import { ConfigService } from '../services/config.service.js';
import { LlmProxyService } from '../services/llm-proxy.service.js';
import { ValidationError } from '../errors/index.js';

function maskKey(key: string): string {
    if (key.length <= 8) return '****';
    return key.slice(0, 8) + '****' + key.slice(-4);
}

/**
 * Handle openai-keys command
 */
async function handleOpenAiKeysCommand(options: { full?: boolean }): Promise<void> {
    const configService = new ConfigService();
    const config = await configService.load();

    if (!config) {
        throw new ValidationError('Not initialized. Run "jai1 auth" first.');
    }

    const service = new LlmProxyService(config);

    // Header
    console.log(
        boxen(chalk.cyan.bold('📡 Jai1 LLM Proxy - OpenAI Compatible API'), {
            padding: { top: 0, bottom: 0, left: 1, right: 1 },
            borderStyle: 'round',
            borderColor: 'cyan',
        })
    );
    console.log();

    // API Credentials
    console.log(chalk.bold('🔑 API Credentials'));
    console.log(`   ${chalk.dim('BASE_URL:')} ${chalk.white(service.getBaseUrl())}`);
    console.log(
        `   ${chalk.dim('API_KEY:')}  ${options.full ? chalk.green(service.getApiKey()) : chalk.yellow(maskKey(service.getApiKey()))}`
    );
    console.log();

    try {
        const [models, limits] = await Promise.all([
            service.getModelsWithUsage(),
            service.getLimits(),
        ]);

        const allowedModels = models.filter((m) => m.allowed);

        // Available Models
        console.log(chalk.bold('📦 Available Models'));
        if (allowedModels.length === 0) {
            console.log(chalk.dim('   Không có models khả dụng'));
        } else {
            for (const model of allowedModels) {
                const usageText =
                    model.dailyLimit !== undefined && model.usedToday !== undefined
                        ? chalk.dim(` (${model.usedToday}/${model.dailyLimit} hôm nay)`)
                        : '';
                console.log(`   ${chalk.green('✓')} ${chalk.white(model.id)}${usageText}`);
            }
        }
        console.log();

        // Sample cURL
        const defaultModel = allowedModels[0]?.id || 'gpt-4o';
        console.log(chalk.bold('📝 Sample cURL'));
        console.log();
        const curlSample = options.full
            ? service.generateFullCurlSample(defaultModel)
            : service.generateCurlSample(defaultModel);
        const curlLines = curlSample.split('\n');
        for (const line of curlLines) {
            console.log(chalk.dim(`   ${line}`));
        }
        console.log();

        // Usage tips
        console.log(chalk.bold('💡 Cách sử dụng'));
        console.log(chalk.dim('   - Thay thế OpenAI API URL và API Key'));
        console.log(chalk.dim('   - Tương thích: OpenAI SDK, LangChain, LlamaIndex, v.v.'));
        console.log(chalk.dim('   - Chạy "jai1 chat" để chat trực tiếp'));
        if (!options.full) {
            console.log(chalk.dim('   - Chạy "jai1 openai-keys --full" để hiển thị API key đầy đủ'));
        }
    } catch (error) {
        console.log();
        console.log(
            chalk.red('❌ Lỗi khi lấy thông tin API:'),
            chalk.dim(error instanceof Error ? error.message : String(error))
        );
        console.log(chalk.dim('\n💡 Kiểm tra API URL và access key với "jai1 status"'));
    }
}

/**
 * Create openai-keys command
 */
export function createOpenAiKeysCommand(): Command {
    const cmd = new Command('openai-keys')
        .description('Show OpenAI-compatible API credentials and info')
        .option('--full', 'Show full API key (unmasked)')
        .action(async (options: { full?: boolean }) => {
            await handleOpenAiKeysCommand(options);
        });

    return cmd;
}
